// @ts-nocheck
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export interface ReactionType {
  id: string;
  emoji: string;
  label: string;
  sort_order: number;
}

export function useReactionTypes() {
  return useQuery({
    queryKey: ['reaction-types'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('wall_reaction_types')
        .select('id, emoji, label, sort_order')
        .eq('is_active', true)
        .order('sort_order');

      if (error) throw error;
      return data as ReactionType[];
    },
    staleTime: 1000 * 60 * 60, // 1 hour - reaction types rarely change
  });
}

export function useUserReaction(postId: string) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['user-reaction', postId, user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('wall_post_reactions')
        .select('id, reaction_type')
        .eq('post_id', postId)
        .eq('profile_id', user.id)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!user?.id,
  });
}

export function usePostReaction(postId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const toggleReaction = useMutation({
    mutationFn: async (reactionType: string) => {
      if (!user?.id) throw new Error('Must be logged in to react');

      // Check if user already has a reaction on this post
      const { data: existing } = await supabase
        .from('wall_post_reactions')
        .select('id, reaction_type')
        .eq('post_id', postId)
        .eq('profile_id', user.id)
        .maybeSingle();

      if (existing) {
        if (existing.reaction_type === reactionType) {
          // Same reaction - remove it
          const { error } = await supabase
            .from('wall_post_reactions')
            .delete()
            .eq('id', existing.id);

          if (error) throw error;
          return { action: 'removed', reactionType };
        } else {
          // Different reaction - update it
          const { error } = await supabase
            .from('wall_post_reactions')
            .update({ reaction_type: reactionType })
            .eq('id', existing.id);

          if (error) throw error;
          return { action: 'updated', reactionType };
        }
      } else {
        // No existing reaction - add new
        const { error } = await supabase
          .from('wall_post_reactions')
          .insert({
            post_id: postId,
            profile_id: user.id,
            reaction_type: reactionType,
          });

        if (error) throw error;
        return { action: 'added', reactionType };
      }
    },
    onSuccess: () => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['user-reaction', postId] });
      queryClient.invalidateQueries({ queryKey: ['wall-feed'] });
    },
    onError: (error) => {
      console.error('Reaction error:', error);
      toast.error('Failed to update reaction');
    },
  });

  return {
    toggleReaction: toggleReaction.mutate,
    isToggling: toggleReaction.isPending,
  };
}
